import React from "react";
import {
  Easing,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { useCopy } from "../copy";
import { LOOK } from "../look";
import { useStage } from "../stage";
import { RawShot } from "../ui/Phone";
import { Tap } from "../ui/bits";
import { AppViewport, ExplorerScreen } from "../ui/AppScreens";
import { PICK_SCROLL } from "./Pick";
import { StepStage, useStepGeom } from "../ui/StepStage";
import { Sfx } from "../../b2b/ui/Sfx";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const OUT = Easing.bezier(0.16, 1, 0.3, 1);

/**
 * Step 2 — share your link. Explorer (coded) → real captures: order confirmed ("Partager et
 * gagner 5 €") → Instagram story with the link sticker ("2 € offerts via
 * mon lien") → send. Friends' avatars pop to show it went out.
 */

// Button / control positions measured on the 786×1704 captures, as
// fractions of the screen.
const SHARE_BTN = { x: 0.5, y: 0.8415 };
const SEND_BTN = { x: 0.92, y: 0.931 };

const PUSH_AT = 0;
const SHARE_TAP = 26;
const STORY_AT = 31;
const SEND_TAP = 70;

const FRIENDS = ["images/scene1/2.jpg", "images/scene1/4.jpg", "images/scene1/6.jpg"];

export const Share: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const stage = useStage();
  const { SCREEN_X, SCREEN_Y, SH, SW, phoneX, phoneY, phoneW, k } = useStepGeom();

  // Explorer → order screen: horizontal push (feels like navigation).
  const push = interpolate(frame, [PUSH_AT, PUSH_AT + 10], [0, 1], {
    ...clamp,
    easing: OUT,
  });
  // Order → story: sheet slides up.
  const story = interpolate(frame, [STORY_AT, STORY_AT + 12], [0, 1], {
    ...clamp,
    easing: OUT,
  });

  return (
    <StepStage
      background={LOOK.sky}
      step={2}
      label={copy.steps.share}
      sub={copy.steps.shareSub}
      labelStart={0}
      screen={
        <>
          <div style={{ position: "absolute", inset: 0, translate: `${-push * SW * 0.3}px 0` }}>
            <AppViewport screenWidth={SW}>
              <ExplorerScreen scrollY={PICK_SCROLL} selected={0.8} />
            </AppViewport>
          </div>
          <div
            style={{
              position: "absolute",
              inset: 0,
              translate: `${(1 - push) * SW}px 0`,
              background: LOOK.white,
              boxShadow: "-20px 0 40px rgba(0,0,0,0.12)",
            }}
          >
            <RawShot src="Order_confirmed.png" screenWidth={SW} />
          </div>
          {frame >= STORY_AT ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                translate: `0 ${(1 - story) * SH}px`,
                background: "#000",
              }}
            >
              <RawShot src="Instagram.png" screenWidth={SW} />
            </div>
          ) : null}
        </>
      }
    >
      <Tap x={SCREEN_X + SHARE_BTN.x * SW} y={SCREEN_Y + SHARE_BTN.y * SH} at={SHARE_TAP} />
      <Tap
        x={SCREEN_X + SEND_BTN.x * SW}
        y={SCREEN_Y + SEND_BTN.y * SH}
        at={SEND_TAP}
        size={70}
      />

      {/* Sent: the link lands with three friends, fanned out right of the phone. */}
      {FRIENDS.map((src, i) => {
        const p = spring({
          frame: frame - SEND_TAP - 4 - i * 4,
          fps,
          config: { damping: 12, stiffness: 220, mass: 0.6 },
        });
        const fromX = SCREEN_X + SEND_BTN.x * SW;
        const fromY = SCREEN_Y + SEND_BTN.y * SH;
        // Just right of the phone (square: x 850/920, y 470..770).
        const toX = Math.min(phoneX + phoneW + 100, stage.w - 230) + (i === 1 ? 70 : 0);
        const toY = phoneY + 232 * k + i * 150;
        return (
          <div
            key={src}
            style={{
              position: "absolute",
              left: interpolate(p, [0, 1], [fromX, toX]) - 60,
              top: interpolate(p, [0, 1], [fromY, toY]) - 60,
              width: 120,
              height: 120,
              scale: interpolate(p, [0, 1], [0.2, 1]),
              opacity: Math.min(1, p * 2),
            }}
          >
            <Img
              src={staticFile(src)}
              style={{
                width: 120,
                height: 120,
                borderRadius: 120,
                objectFit: "cover",
                border: `6px solid ${LOOK.white}`,
                boxShadow: "0 12px 24px -8px rgba(0, 40, 143, 0.4)",
              }}
            />
            <div
              style={{
                position: "absolute",
                right: -6,
                bottom: -2,
                width: 46,
                height: 46,
                borderRadius: 46,
                background: LOOK.blue,
                border: `4px solid ${LOOK.white}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1 1M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1-1"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
        );
      })}

      {/* Tap "share", tap "send", the link leaves for the friends. */}
      <Sfx sound="click" at={SHARE_TAP} />
      <Sfx sound="click" at={SEND_TAP} />
      <Sfx sound="bubble-out" at={SEND_TAP + 8} />
    </StepStage>
  );
};
